import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, FormGroup, Input, Label, Row, Alert, TabContent, TabPane } from 'reactstrap';
import { fetchCompanyList } from 'components/companyList/redux/dispatcher';
import { fetchMachineListForBulkUpload } from 'components/report/redux/dispatcher';
import { serverCall } from 'utils/serverCall';
import { FiUpload, FiX, FiFileText, FiInfo } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import apiEndpoints from '../../utils/apiList';

const BulkUploadReport = () => {
    const dispatch = useDispatch();
    const API_BASE_URL = process.env.REACT_APP_BACKEND_API_BASE_URL;
    const companyListData = useSelector((state) => state.companyListReducer?.data?.data) || [];
    const machineListFromStore = useSelector((state) => state.reportReducer?.machineByCompanyIdforreport) || [];
    const [companyId, setCompanyId] = useState('');
    const [oilType, setOilType] = useState('coolant');
    const [machineIds, setMachineIds] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [machineSearch, setMachineSearch] = useState('');
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [loading, setLoading] = useState(false);
    const [templateGenerated, setTemplateGenerated] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [generateError, setGenerateError] = useState('');
    const [generateSuccess, setGenerateSuccess] = useState('');
    const [uploadError, setUploadError] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState('');
    const [activeTab, setActiveTab] = useState('generate');
    const [dataType, setDataType] = useState('all');

    // Function to clear all alert messages
    const clearAllAlerts = () => {
        setGenerateError('');
        setGenerateSuccess('');
        setUploadError('');
        setUploadSuccess('');
    };

    // Function to handle tab switching with alert clearing
    const handleTabSwitch = (tabId) => {
        clearAllAlerts();
        setActiveTab(tabId);
    };

    // Fetch company list on mount
    useEffect(() => {
        dispatch(fetchCompanyList());
    }, [dispatch]);

    // Fetch machines when company or oil type changes
    useEffect(() => {
        if (companyId && oilType) {
            dispatch(fetchMachineListForBulkUpload(companyId, oilType));
        } else {
            setMachineIds([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, oilType, dispatch]);

    // Filter machines based on search
    const filteredMachineList = machineListFromStore.filter(m =>
        m?.name?.toLowerCase().includes(machineSearch.toLowerCase())
    );

    // Toggle individual machine selection
    const toggleMachine = (serialNumber) => {
        setMachineIds(prev =>
            prev.includes(serialNumber) ? prev.filter(m => m !== serialNumber) : [...prev, serialNumber]
        );
    };

    // Toggle select all machines
    const toggleSelectAll = () => {
        if (isAllSelected) {
            setMachineIds([]);
        } else {
            setMachineIds(filteredMachineList.map(m => m.serial_number));
        }
    };

    // Update isAllSelected when machineIds or filteredMachineList changes
    useEffect(() => {
        setIsAllSelected(
            machineIds.length > 0 &&
            machineIds.length === filteredMachineList.length &&
            filteredMachineList.length > 0
        );
    }, [machineIds, filteredMachineList]);

    // File validation function
    const validateFileStructure = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });

                    // Check if file has any sheets
                    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
                        reject('No worksheets found in the file');
                        return;
                    }

                    // Expected required headers for routine work
                    const expectedHeaders = [
                        'Date', 'Company Name', 'Machine No', 'Email', 'Water pH',
                        'TDS', 'Hardness CaCo3', 'Chloride PPM',
                        'Emulsion Hardness', 'Expected Sump Life', 'Batch No',
                        '% Brix Conc Before', 'Water Added', 'Oil Added',
                        '% Brix Conc After', 'Emulsion pH'
                    ];

                    let validSheets = 0;
                    let totalDataRows = 0;
                    let sheetsInfo = [];

                    // Validate each sheet
                    for (const sheetName of workbook.SheetNames) {
                        const worksheet = workbook.Sheets[sheetName];
                        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                        if (jsonData.length === 0) {
                            continue; // Skip empty sheets
                        }

                        // Check header row
                        const headerRow = jsonData[0];
                        if (!headerRow || headerRow.length === 0) {
                            continue; // Skip sheets without headers
                        }

                        // Check if required headers are present
                        const missingHeaders = expectedHeaders.filter(header =>
                            !headerRow.some(cellValue =>
                                cellValue && cellValue.toString().trim().toLowerCase() === header.toLowerCase()
                            )
                        );

                        // Check if there are data rows
                        const dataRows = jsonData.slice(1);
                        const validDataRows = dataRows.filter(row =>
                            row && row.length > 0 && row.some(cell => cell !== null && cell !== undefined && cell !== '')
                        );

                        if (validDataRows.length > 0) {
                            validSheets++;
                            totalDataRows += validDataRows.length;
                            sheetsInfo.push({
                                name: sheetName,
                                headers: headerRow,
                                dataRows: validDataRows.length,
                                missingHeaders: missingHeaders
                            });
                        }
                    }

                    if (validSheets === 0) {
                        reject('No valid sheets with routine work data found. Please ensure at least one sheet has the correct headers and data.');
                        return;
                    }

                    if (totalDataRows === 0) {
                        reject('No data rows found in any sheet. Please ensure there is data below the header row.');
                        return;
                    }

                    resolve({
                        sheets: sheetsInfo,
                        totalSheets: validSheets,
                        totalDataRows: totalDataRows,
                        sheetNames: workbook.SheetNames
                    });

                } catch (error) {
                    reject(`Error reading file: ${error.message}`);
                }
            };
            reader.onerror = () => reject('Error reading file');
            reader.readAsArrayBuffer(file);
        });
    };

    // Handle file change
    const handleFileChange = async (e) => {
        const file = e.target.files[0];

        // Clear previous states first
        setUploadError('');
        setUploadSuccess('');

        if (!file) {
            setSelectedFile(null);
            return;
        }

        setSelectedFile(file);

        // Only validate file structure without showing errors on file selection
        if (file) {
            try {
                const info = await validateFileStructure(file);
                console.log('File info loaded:', info);
            } catch (validationError) {
                console.warn('File validation warning:', validationError);
                // Don't set error here, validation will happen again on upload
            }
        }
    };

    // Handle file import
    const handleImport = async () => {
        // Clear previous states immediately
        setUploadError('');
        setUploadSuccess('');

        if (!selectedFile) {
            setUploadError('Please select a file to import');
            return;
        }

        // Validate file type and extension
        const fileName = selectedFile.name.toLowerCase();
        const allowedExtensions = ['xlsx', 'xls', 'csv'];
        const fileExtension = fileName.split('.').pop();

        if (!allowedExtensions.includes(fileExtension)) {
            setUploadError('Please select a valid Excel file (.xlsx, .xls) or CSV file (.csv)');
            return;
        }

        // Check file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (selectedFile.size > maxSize) {
            setUploadError('File size must be less than 10MB');
            return;
        }

        // Check if file is not empty
        if (selectedFile.size === 0) {
            setUploadError('Please select a non-empty file');
            return;
        }

        setUploading(true);

        try {
            // Validate file structure first
            const fileValidation = await validateFileStructure(selectedFile);
            console.log('File validation result:', fileValidation);

            const formData = new FormData();
            formData.append('file', selectedFile);

            const result = await serverCall(
                apiEndpoints.ROUTINE_WORK_UPLOAD,
                "POST",
                dispatch,
                formData
            );

            if (result.success) {
                setUploadSuccess('File imported successfully!');
            } else {
                const errorMessage = result.message || 'Failed to import file. Please try again.';
                setUploadError(errorMessage);
                console.error('Backend error:', result);
            }

        } catch (err) {
            let errorMessage = 'An error occurred while uploading the file';

            if (err.message) {
                errorMessage = err.message;
            } else if (typeof err === 'string') {
                errorMessage = err;
            }

            setUploadError(errorMessage);
            console.error('Upload error:', err);
        } finally {
            setUploading(false);
            setSelectedFile(null);
            if (document.getElementById('fileInputBulk')) {
                document.getElementById('fileInputBulk').value = '';
            }
            if (document.getElementById('fileInputBulkDirect')) {
                document.getElementById('fileInputBulkDirect').value = '';
            }
        }
    };

    // Handle form generation
    const handleGenerate = async () => {
        // Clear previous generate states
        setGenerateError('');
        setGenerateSuccess('');

        // Validation
        if (!companyId) {
            setGenerateError('Please select a company');
            return;
        }
        if (!oilType) {
            setGenerateError('Please select oil type');
            return;
        }
        if (machineIds.length === 0) {
            setGenerateError('Please select at least one machine');
            return;
        }
        if (!startDate || !endDate) {
            setGenerateError('Please select both start and end dates');
            return;
        }
        if (new Date(startDate) > new Date(endDate)) {
            setGenerateError('Start date cannot be after end date');
            return;
        }

        // Get the email from selected fluid handler
        // const email = selectedFH?.email || '';

        console.log(machineIds, "Machine IDs selected (serial_numbers)");

        // Build query parameters
        const params = new URLSearchParams({
            company_id: companyId,
            oil_type: oilType,
            machine_ids: machineIds.join(','),
            from_date: startDate,
            to_date: endDate,
            // email: email
        });

        const endpoint = dataType === 'missing' ? 'fh/template/data' : 'fh/template';
        const url = `${API_BASE_URL}${endpoint}?${params.toString()}`;
        console.log('Calling template API:', url);

        setLoading(true);
        try {
            // Make API call with blob response type for file download
            const token = localStorage.getItem('authToken');
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                setGenerateError(errorData.message || 'Failed to generate template');
                return;
            }

            // Response status is 200, show file upload section
            setTemplateGenerated(true);

            // Check if response is a file (blob)
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                // If JSON response, check for file URL
                const data = await response.json();
                if (data.success && data.data?.fileUrl) {
                    // Download from URL
                    const link = document.createElement('a');
                    link.href = data.data.fileUrl;
                    link.download = `${oilType} oil-${startDate}-${endDate}.xlsx`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setGenerateSuccess('Template generated and downloaded successfully! You can now upload the filled template.');
                } else {
                    setGenerateSuccess('Template generated successfully! You can now upload the filled template.');
                }
            } else {
                // Response is a file blob, download it directly
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;

                // Extract filename from Content-Disposition header or use default
                const contentDisposition = response.headers.get('Content-Disposition');
                const displayOilType = oilType === 'maintenance' ? 'Neat' : oilType;
                let filename = `${displayOilType} oil-${startDate}-${endDate}.xlsx`;
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }

                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
                setGenerateSuccess('Template downloaded successfully! You can now upload the filled template.');
            }

            // Clear all input values after successful generation
            setCompanyId('');
            setOilType('coolant');
            setMachineIds([]);
            setStartDate('');
            setEndDate('');
            setMachineSearch('');
            setIsAllSelected(false);

            // Don't automatically switch tabs - let user manually switch if needed
        } catch (error) {
            console.error('Error generating template:', error);
            setGenerateError('Failed to generate template');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            {/* Custom Tab Navigation */}
            <div style={{
                display: 'flex',
                gap: '10px',
                marginBottom: '25px',
                borderBottom: '2px solid #e9ecef',
                paddingBottom: '0',
                marginTop: '12px'
            }}>
                <button
                    onClick={() => handleTabSwitch('generate')}
                    style={{
                        flex: 1,
                        padding: '6px 6px',
                        background: activeTab === 'generate' ? 'rgb(14, 61, 13)' : 'transparent',
                        color: activeTab === 'generate' ? '#fff' : '#495057',
                        border: 'none',
                        borderRadius: '8px 8px 0 0',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        bottom: '-2px',
                        borderBottom: activeTab === 'generate' ? '3px solid rgb(14, 61, 13)' : '3px solid transparent',
                        boxShadow: activeTab === 'generate' ? '0 -2px 8px rgba(14, 61, 13, 0.2)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                        if (activeTab !== 'generate') {
                            e.target.style.background = '#f8f9fa';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (activeTab !== 'generate') {
                            e.target.style.background = 'transparent';
                        }
                    }}
                >
                    Generate Template
                </button>
                <button
                    onClick={() => handleTabSwitch('bulk-upload')}
                    style={{
                        flex: 1,
                        padding: '6px 6px',
                        background: activeTab === 'bulk-upload' ? 'rgb(14, 61, 13)' : 'transparent',
                        color: activeTab === 'bulk-upload' ? '#fff' : '#495057',
                        border: 'none',
                        borderRadius: '8px 8px 0 0',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        bottom: '-2px',
                        borderBottom: activeTab === 'bulk-upload' ? '3px solid rgb(14, 61, 13)' : '3px solid transparent',
                        boxShadow: activeTab === 'bulk-upload' ? '0 -2px 8px rgba(19, 86, 116, 0.2)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                        if (activeTab !== 'bulk-upload') {
                            e.target.style.background = '#f8f9fa';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (activeTab !== 'bulk-upload') {
                            e.target.style.background = 'transparent';
                        }
                    }}
                >
                    Excel Upload
                </button>
            </div>

            {/* Tab Content */}
            <TabContent activeTab={activeTab}>
                {/* Generate Tab */}
                <TabPane tabId="generate">
                    <Row className="mb-4 mt-2">
                        <Col md="12" className="d-flex justify-content-start align-items-center">
                            <span style={{ marginRight: '12px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                Template Data:
                            </span>
                            <div style={{
                                display: 'inline-flex',
                                background: '#f1f3f5',
                                borderRadius: '8px',
                                padding: '4px',
                                border: '1px solid #e9ecef'
                            }}>
                                <button
                                    type="button"
                                    onClick={() => setDataType('all')}
                                    style={{
                                        border: 'none',
                                        background: dataType === 'all' ? '#fff' : 'transparent',
                                        color: dataType === 'all' ? 'rgb(14, 61, 13)' : '#6c757d',
                                        padding: '6px 20px',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        fontSize: '13px',
                                        transition: 'all 0.2s ease',
                                        boxShadow: dataType === 'all' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
                                    }}
                                >
                                    All Data
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDataType('missing')}
                                    style={{
                                        border: 'none',
                                        background: dataType === 'missing' ? '#fff' : 'transparent',
                                        color: dataType === 'missing' ? 'rgb(14, 61, 13)' : '#6c757d',
                                        padding: '6px 20px',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        fontSize: '13px',
                                        transition: 'all 0.2s ease',
                                        boxShadow: dataType === 'missing' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
                                    }}
                                >
                                    Missing Data
                                </button>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup className="mb-2">
                                <Label className="mb-1">Select Company<span className='text-danger'>*</span></Label>
                                <Input
                                    type="select"
                                    value={companyId}
                                    onChange={e => {
                                        setCompanyId(e.target.value);
                                        setMachineIds([]);
                                    }}
                                    style={{ padding: '6px 12px', fontSize: '14px' }}
                                >
                                    <option value="">Select Company</option>
                                    {companyListData.map(c => (
                                        <option key={c.id} value={c.id}>{c.company_name}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="mb-2">
                                <Label className="mb-1">Oil Type<span className='text-danger'>*</span></Label>
                                <Input
                                    type="select"
                                    value={oilType}
                                    onChange={e => {
                                        setOilType(e.target.value);
                                        setMachineIds([]);
                                    }}
                                    style={{ padding: '6px 12px', fontSize: '14px' }}
                                >
                                    <option value="coolant">Coolant</option>
                                    <option value="hydraulic">Hydraulic</option>
                                    <option value="maintenance">Neat</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup className="mb-2">
                                <Label className="mb-1">Select Machines {machineIds.length > 0 && `(${machineIds.length} Selected)`}<span className='text-danger'>*</span></Label>
                                <div style={{ border: '1px solid #ced4da', borderRadius: 4, padding: 6, background: '#fff', maxHeight: 300, overflowY: 'auto' }}>
                                    {!companyId ? (
                                        <div className="text-muted text-center" style={{ padding: '20px', fontSize: '14px' }}>
                                            Select company to see machine list
                                        </div>
                                    ) : (
                                        <>
                                            <div className="mb-2">
                                                <Input
                                                    placeholder="Search Machine"
                                                    value={machineSearch}
                                                    onChange={e => setMachineSearch(e.target.value)}
                                                    style={{ padding: '4px 8px', fontSize: '14px' }}
                                                />
                                            </div>
                                            <div className="mb-2">
                                                <div className="custom-control custom-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        className="custom-control-input"
                                                        id="selectAllMachinesBulk"
                                                        checked={isAllSelected}
                                                        onChange={toggleSelectAll}
                                                    />
                                                    <label
                                                        className="custom-control-label"
                                                        htmlFor="selectAllMachinesBulk"
                                                        style={{ fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}
                                                    >
                                                        {isAllSelected ? `Clear All (${filteredMachineList.length})` : `Select All (${filteredMachineList.length})`}
                                                    </label>
                                                </div>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                                                {filteredMachineList.map(m => {
                                                    const serialNumber = m.serial_number;
                                                    const id = m.id || m.machine_id;
                                                    return (
                                                        <div key={id} className="custom-control custom-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id={`mach-bulk-${id}`}
                                                                value={serialNumber}
                                                                checked={machineIds.includes(serialNumber)}
                                                                onChange={() => toggleMachine(serialNumber)}
                                                            />
                                                            <label className="custom-control-label" htmlFor={`mach-bulk-${id}`} style={{ fontSize: '14px' }}>
                                                                {m.name} ({serialNumber || id})
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {filteredMachineList.length === 0 && (
                                                <div className="text-muted small">No machines found</div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* <Row>
                        <Col md="12">
                            <FormGroup className="mb-2">
                                <Label className="mb-1">Select Fluid Handler<span className='text-danger'>*</span></Label>
                                <Input
                                    type="select"
                                    value={selectedFluidHandler}
                                    onChange={e => setSelectedFluidHandler(e.target.value)}
                                    style={{ padding: '6px 12px', fontSize: '14px' }}
                                    disabled={!companyId || loading}
                                >
                                    <option value="">
                                        {loading ? 'Loading fluid handlers...' : 'Select Fluid Handler'}
                                    </option>
                                    {Array.isArray(fluidHandlers) && fluidHandlers.length > 0 ? (
                                        fluidHandlers.map(fh => {
                                            console.log('Mapping fluid handler:', fh);
                                            return (
                                                <option key={fh.uuid} value={fh.uuid}>
                                                    {fh.full_name || fh.first_name + ' ' + fh.last_name || 'Unnamed'}
                                                </option>
                                            );
                                        })
                                    ) : (
                                        !loading && <option disabled>No fluid handlers found</option>
                                    )}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row> */}

                    <Row>
                        <Col md="6">
                            <FormGroup className="mb-2">
                                <Label className="mb-1">Start Date<span className='text-danger'>*</span></Label>
                                <Input
                                    type="date"
                                    value={startDate}
                                    onChange={e => setStartDate(e.target.value)}
                                    max={new Date().toISOString().split('T')[0]}
                                    style={{ padding: '6px 12px', fontSize: '14px' }}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="mb-2">
                                <Label className="mb-1">End Date<span className='text-danger'>*</span></Label>
                                <Input
                                    type="date"
                                    value={endDate}
                                    onChange={e => setEndDate(e.target.value)}
                                    max={new Date().toISOString().split('T')[0]}
                                    style={{ padding: '6px 12px', fontSize: '14px' }}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    {generateError && (
                        <Alert color="danger" className="mb-3">
                            <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                    <strong>Error:</strong> {generateError}
                                    {(generateError.includes('login again') || generateError.includes('Authentication') || generateError.includes('session')) && (
                                        <div className="mt-2">
                                            <small>
                                                Please refresh the page and login again to continue.
                                            </small>
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={() => setGenerateError('')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        marginLeft: '10px',
                                        opacity: '0.7',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <FiX size={16} />
                                </button>
                            </div>
                        </Alert>
                    )}

                    {generateSuccess && (
                        <Alert color="success" className="mb-3">
                            <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                    <strong>Success:</strong> {generateSuccess}
                                </div>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={() => setGenerateSuccess('')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        marginLeft: '10px',
                                        opacity: '0.7',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <FiX size={16} />
                                </button>
                            </div>
                        </Alert>
                    )}

                    <Row>
                        <Col className="text-center mt-3">
                            <Button
                                style={{ background: 'rgb(14, 61, 13)' }}
                                disabled={
                                    !companyId ||
                                    !oilType ||
                                    machineIds.length === 0 ||
                                    !startDate ||
                                    !endDate ||
                                    loading ||
                                    generateError
                                }
                                onClick={handleGenerate}
                            >
                                Generate
                            </Button>
                        </Col>
                    </Row>
                </TabPane>

                {/* Bulk Upload Tab */}
                <TabPane tabId="bulk-upload">
                    <div style={{
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        padding: '30px',
                        borderRadius: '10px',
                        border: '2px dashed #6c757d'
                    }}>

                        <Alert color="info" className="mb-4" style={{ background: '#e7f3ff', border: '1px solid #b3d9ff' }}>
                            <div className="d-flex align-items-start">
                                <div style={{ color: '#004085' }}>
                                    <strong>Info:</strong> If you already have a filled template from a previous generation,
                                    you can upload it directly here.
                                    <div className="mt-2">
                                        <small>
                                            <strong>Supported formats:</strong> .xlsx, .xls, .csv
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </Alert>

                        <Row className="mb-3">
                            <Col md="12">
                                <FormGroup>
                                    <Label for="fileInputBulkDirect" className="font-weight-bold mb-2">
                                        <FiUpload className="me-2" size={18} />
                                        Choose File to Import <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        type="file"
                                        id="fileInputBulkDirect"
                                        onChange={handleFileChange}
                                        accept=".xlsx,.xls,.csv"
                                        disabled={uploading}
                                        className="form-control"
                                    />
                                    {selectedFile && (
                                        <small className="text-muted d-block mt-2">
                                            <FiFileText className="me-1" size={14} />
                                            Selected: {selectedFile.name}
                                        </small>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>

                        {uploadError && (
                            <Alert color="danger" className="mb-3">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                        <strong>Error:</strong> {uploadError}
                                        {(uploadError.includes('login again') || uploadError.includes('Authentication') || uploadError.includes('session')) && (
                                            <div className="mt-2">
                                                <small>
                                                    Please refresh the page and login again to continue.
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setUploadError('')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            marginLeft: '10px',
                                            opacity: '0.7',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <FiX size={16} />
                                    </button>
                                </div>
                            </Alert>
                        )}

                        {uploadSuccess && (
                            <Alert color="success" className="mb-3">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                        <strong>Success:</strong> {uploadSuccess}
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setUploadSuccess('')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            marginLeft: '10px',
                                            opacity: '0.7',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <FiX size={16} />
                                    </button>
                                </div>
                            </Alert>
                        )}

                        <Row>
                            <Col className="text-center">
                                <Button
                                    style={{
                                        background: 'rgb(14, 61, 13)',
                                        border: 'none',
                                        padding: '12px 40px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                    }}
                                    onClick={handleImport}
                                    disabled={!selectedFile || uploading}
                                    className="d-flex align-items-center mx-auto"
                                >
                                    {uploading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <FiUpload className="me-2" size={18} />
                                            <span>Upload File</span>
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </div>
                </TabPane>
            </TabContent>
        </div>
    );
};

export default BulkUploadReport;
